export type VehicleHandoff = 'drop_off' | 'wait';

export interface AutomotiveIntakeData {
  vehicleHandoff: VehicleHandoff | null;
  year: string;
  make: string;
  model: string;
  licensePlate: string;
  vin: string;
  additionalDetails: string;
}

export function emptyAutomotiveIntake(): AutomotiveIntakeData {
  return {
    vehicleHandoff: null,
    year: '',
    make: '',
    model: '',
    licensePlate: '',
    vin: '',
    additionalDetails: '',
  };
}

export function validateAutomotiveIntake(data: AutomotiveIntakeData): string | null {
  if (!data.vehicleHandoff) {
    return 'Please choose whether you are dropping off your vehicle or waiting on-site.';
  }
  if (!data.year.trim()) return 'Please enter your vehicle year.';
  if (!data.make.trim()) return 'Please enter your vehicle make.';
  if (!data.model.trim()) return 'Please enter your vehicle model.';
  if (!data.licensePlate.trim()) return 'Please enter your license plate number.';
  return null;
}

export function automotiveIntakeFromVehicle(vehicle: {
  year?: string;
  make?: string;
  model?: string;
  plate?: string;
  vin?: string;
}): Partial<AutomotiveIntakeData> {
  return {
    year: (vehicle.year || '').trim(),
    make: (vehicle.make || '').trim(),
    model: (vehicle.model || '').trim(),
    licensePlate: (vehicle.plate || '').trim(),
    vin: (vehicle.vin || '').trim(),
  };
}

export function formatVehicleHandoffLabel(handoff: VehicleHandoff | null | undefined): string {
  if (handoff === 'drop_off') return 'Drop off vehicle';
  if (handoff === 'wait') return 'Wait on-site';
  return 'Not specified';
}

export function mapAutomotiveIntakeToAppointmentFields(data: AutomotiveIntakeData) {
  return {
    vehicleHandoff: data.vehicleHandoff || undefined,
    vehicleYear: data.year.trim(),
    vehicleMake: data.make.trim(),
    vehicleModel: data.model.trim(),
    vehicleLicensePlate: data.licensePlate.trim(),
    vehicleVin: data.vin.trim() || undefined,
    vinMethod: data.vin.trim() ? ('typed' as const) : undefined,
  };
}

/** Sentinel value when the customer is entering vehicle details manually. */
export const MANUAL_VEHICLE_ENTRY = '__manual__';

export function vehicleMatchesAutomotiveIntake(
  vehicle: { year?: string; make?: string; model?: string; plate?: string },
  data: AutomotiveIntakeData,
): boolean {
  const plate = (vehicle.plate || '').trim().toUpperCase();
  const intakePlate = data.licensePlate.trim().toUpperCase();
  if (plate && intakePlate && plate === intakePlate) return true;
  const y = (vehicle.year || '').trim();
  const mk = (vehicle.make || '').trim().toLowerCase();
  const md = (vehicle.model || '').trim().toLowerCase();
  return (
    y === data.year.trim() &&
    mk === data.make.trim().toLowerCase() &&
    md === data.model.trim().toLowerCase()
  );
}

export function automotiveIntakeToGarageInput(
  userId: string,
  data: AutomotiveIntakeData,
  options?: { isDefault?: boolean },
) {
  return {
    userId,
    year: data.year.trim(),
    make: data.make.trim(),
    model: data.model.trim(),
    plate: data.licensePlate.trim(),
    vin: data.vin.trim() || undefined,
    isDefault: options?.isDefault,
  };
}

export function appendAutomotiveDetailsToInstructions(
  baseInstructions: string,
  data: AutomotiveIntakeData,
): string {
  const parts: string[] = [];
  if (baseInstructions.trim()) {
    parts.push(baseInstructions.trim());
  }
  parts.push(`Service visit: ${formatVehicleHandoffLabel(data.vehicleHandoff)}`);
  parts.push(
    `Vehicle: ${data.year.trim()} ${data.make.trim()} ${data.model.trim()} | Plate ${data.licensePlate.trim()}`,
  );
  if (data.vin.trim()) {
    parts.push(`VIN: ${data.vin.trim()}`);
  }
  if (data.additionalDetails.trim()) {
    parts.push(`Notes: ${data.additionalDetails.trim()}`);
  }
  return parts.join(' || ');
}
